import { z } from 'zod';
import { getTraceId } from 'src/lib/trace';

type SentryDsnParts = {
  endpoint: string;
  projectId: string;
  publicKey: string;
};

type Breadcrumb = {
  category: string;
  message: string;
  level: 'info' | 'error';
  timestamp: number;
  data?: Record<string, unknown>;
};


type IgnoreRule = {
  id: string;
  messagePattern?: RegExp;
  stackPattern?: RegExp;
};

const FRONTEND_NOISE_RULES: IgnoreRule[] = [
  {
    id: 'browser_extension.chrome',
    messagePattern: /chrome-extension:\/\//i,
    stackPattern: /chrome-extension:\/\//i,
  },
  {
    id: 'browser_extension.firefox',
    messagePattern: /moz-extension:\/\//i,
    stackPattern: /moz-extension:\/\//i,
  },
  {
    id: 'browser_extension.safari',
    messagePattern: /safari-web-extension:\/\//i,
    stackPattern: /safari-web-extension:\/\//i,
  },
];

const emptyStringAsUndefined = (value: unknown): unknown =>
  typeof value === 'string' && value.trim().length === 0 ? undefined : value;

const ErrorTrackingEnvSchema = z.object({
  dsn: z.preprocess(emptyStringAsUndefined, z.string().trim().min(1).optional()),
  environment: z.preprocess(emptyStringAsUndefined, z.string().trim().min(1).default('local')),
  release: z.preprocess(emptyStringAsUndefined, z.string().trim().min(1).optional()),
  eventSampleRate: z.preprocess(emptyStringAsUndefined, z.coerce.number().min(0).max(1).default(1)),
  wsHighFreqSampleRate: z.preprocess(emptyStringAsUndefined, z.coerce.number().min(0).max(1).default(0.05)),
  wsCapturePayload: z.preprocess(
    emptyStringAsUndefined,
    z
      .string()
      .optional()
      .transform((value) => value === 'true')
      .default('false'),
  ),
});

export type ErrorTrackingConfig = z.infer<typeof ErrorTrackingEnvSchema>;

const SECRET_KEYS = ['authorization', 'cookie', 'set-cookie', 'lease_token', 'token', 'x-queue-control-token'];
const breadcrumbs: Breadcrumb[] = [];
let currentConfig: ErrorTrackingConfig | null = null;
let dsnParts: SentryDsnParts | null = null;

const scrub = (value: unknown): unknown => {
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(scrub);
  }
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, inner]) => {
    const lowered = key.toLowerCase();
    const isSecret = SECRET_KEYS.some((segment) => lowered.includes(segment));
    acc[key] = isSecret ? '[REDACTED]' : scrub(inner);
    return acc;
  }, {});
};

const parseSentryDsn = (dsn: string): SentryDsnParts => {
  const parsed = new URL(dsn);
  const publicKey = parsed.username;
  const pathParts = parsed.pathname.split('/').filter(Boolean);
  const projectId = pathParts[pathParts.length - 1];
  const basePath = pathParts.slice(0, -1).join('/');
  const prefix = basePath ? `/${basePath}` : '';

  return {
    endpoint: `${parsed.protocol}//${parsed.host}${prefix}/api/${projectId}/store/`,
    projectId,
    publicKey,
  };
};

const shouldSendEvent = (): boolean => {
  if (!currentConfig || !currentConfig.dsn) {
    return false;
  }
  return Math.random() <= currentConfig.eventSampleRate;
};


const resolveErrorDetails = (error: unknown): { message: string; stack: string | undefined } => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    };
  }
  return {
    message: String(error),
    stack: undefined,
  };
};

const findIgnoreRule = (error: unknown): IgnoreRule | null => {
  const details = resolveErrorDetails(error);
  for (const rule of FRONTEND_NOISE_RULES) {
    const messageMatches = rule.messagePattern ? rule.messagePattern.test(details.message) : false;
    const stackMatches = rule.stackPattern && details.stack ? rule.stackPattern.test(details.stack) : false;
    if (messageMatches || stackMatches) {
      return rule;
    }
  }
  return null;
};

const buildEventPayload = (error: unknown, context: Record<string, unknown>) => {
  const traceId = getTraceId();
  const { message: errorMessage } = resolveErrorDetails(error);

  return scrub({
    event_id: traceId.replace(/-/g, '').slice(0, 32),
    timestamp: new Date().toISOString(),
    level: 'error',
    platform: 'javascript',
    logger: 'dashboard',
    environment: currentConfig?.environment,
    release: currentConfig?.release,
    message: errorMessage,
    tags: {
      service: 'dashboard',
      env: currentConfig?.environment,
      trace_id: traceId,
    },
    contexts: {
      dwellbot: {
        trace_id: traceId,
        ...context,
      },
    },
    breadcrumbs: {
      values: breadcrumbs.slice(-50),
    },
  });
};

const postErrorEvent = async (payload: unknown): Promise<void> => {
  if (!dsnParts || !shouldSendEvent()) {
    return;
  }

  const sentryVersion = '7';
  const sentryClient = 'dwellbot-dashboard/1.0';
  const timestamp = Math.floor(Date.now() / 1000);

  const auth = [
    `Sentry sentry_version=${sentryVersion}`,
    `sentry_key=${dsnParts.publicKey}`,
    `sentry_timestamp=${timestamp}`,
    `sentry_client=${sentryClient}`,
  ].join(', ');

  await fetch(dsnParts.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Sentry-Auth': auth,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
};

export const parseErrorTrackingEnv = (): ErrorTrackingConfig =>
  ErrorTrackingEnvSchema.parse({
    dsn: process.env.REACT_APP_BSTACK_ERROR_TRACKING_DSN,
    environment: process.env.REACT_APP_BSTACK_ERROR_TRACKING_ENV ?? process.env.NODE_ENV,
    release: process.env.REACT_APP_BSTACK_ERROR_TRACKING_RELEASE,
    eventSampleRate: process.env.REACT_APP_BSTACK_ERROR_EVENT_SAMPLE_RATE ?? '1',
    wsHighFreqSampleRate: process.env.REACT_APP_BSTACK_WS_SAMPLE_RATE_HIGH_FREQ ?? '0.05',
    wsCapturePayload: process.env.REACT_APP_BSTACK_WS_CAPTURE_PAYLOAD,
  });

export const initErrorTracking = (): ErrorTrackingConfig => {
  currentConfig = parseErrorTrackingEnv();
  dsnParts = currentConfig.dsn ? parseSentryDsn(currentConfig.dsn) : null;
  return currentConfig;
};

export const addErrorBreadcrumb = (message: string, data?: Record<string, unknown>): void => {
  breadcrumbs.push({
    category: 'websocket',
    message,
    level: 'info',
    timestamp: Date.now() / 1000,
    data: scrub(data) as Record<string, unknown> | undefined,
  });
  if (breadcrumbs.length > 50) {
    breadcrumbs.splice(0, breadcrumbs.length - 50);
  }
};


export const captureUiError = (error: unknown, context: Record<string, unknown>): void => {
  const ignoredRule = findIgnoreRule(error);
  if (ignoredRule) {
    return;
  }

  const payload = buildEventPayload(error, context);
  void postErrorEvent(payload);
};
